import { useEffect, useState } from "react";

import { Box, Button, Checkbox, CheckboxGroup, Divider, Flex, FormControl, FormErrorMessage, FormLabel, HStack, Icon, Input, Text, useDisclosure, useToast, Textarea } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { FaEye } from "react-icons/fa";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectApprovalStatus from "@/components/Selects/SelectApprovalStatus";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { reviewVesselSchema } from "@/schemas/reviewVesselSchema";
import { getVesselById, updateStatusVessel } from "@/services/vesselService";
import { downloadFile } from "@/services/downloadService";

const EditVesselShipOwner = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id, idShipOwner } = location.state || {};

    const [dataEdit, setDataEdit] = useState(null) as any;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [defaultType, setDefaultType] = useState<string[]>([]);

    const handleCancel = () => {
        if (dataDetail?.status === 2 && role === 'ship operator') {
            navigate('/manage-vessel/');
            return;
        }

        onOpen();
        setTitle("Cancel to Update Status?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Update Status?");
        setConfirmationTest("Are you sure you want to update?");
        setConfirmationTitleButton("Update Status");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateData = () => {
        const obj = {
            vesselId: id,
            registerStatus: parseInt(dataEdit.status),
            note: dataEdit.note,
            modifiedBy: name || "System"
        };
        return obj;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Update Status?") {
            if (role === 'super admin') {
                navigate('/manage-ship-operator/vessel', { state: { idShipOwner } })
            } else {
                navigate('/manage-vessel');
            }
            return;
        }

        const obj = generateData();
        const response = await updateStatusVessel(obj) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Status updated.',
                description: "We've updated the status for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            setTimeout(() => {
                if (role === 'super admin') {
                    navigate('/manage-ship-operator/vessel', { state: { idShipOwner } })
                } else {
                    navigate('/manage-vessel');
                }
            }, 1000)
        } else {
            toast({
                title: 'Status failed to updated.',
                description: response.message ?? "We unabled to update the status for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const getDataDetail = async () => {
        const response = await getVesselById(id) as any;
        if (response) {
            setDataDetail(response.data);
            const newDefaultType: string[] = [];

            if (response.data.sirebire) {
                newDefaultType.push('SIRE | BIRE');
            }

            if (response.data.vetting) {
                newDefaultType.push('Vetting');
            }

            setDefaultType(newDefaultType);
        }
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        imo: dataDetail?.imo ?? '',
                        imoRegNumber: dataDetail?.imoreg ?? '',
                        vesselName: dataDetail?.vesselName ?? '',
                        type: dataDetail ? defaultType : [],
                        status: '',
                        smc: null,
                        note: dataDetail?.note ?? ''
                    }}
                    validationSchema={reviewVesselSchema}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form style={{ marginTop: 20 }}>
                            <Field name='type'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.type && form.touched.type} marginBottom={4}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="type" className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CheckboxGroup
                                                    value={props.values.type}
                                                    onChange={(value: string[]) => props.setFieldValue('type', value)}
                                                    isDisabled
                                                >
                                                    <Flex gap={8}>
                                                        <Checkbox value="SIRE | BIRE">SIRE | BIRE</Checkbox>
                                                        <Checkbox value="Vetting">Vetting</Checkbox>
                                                    </Flex>
                                                </CheckboxGroup>
                                                <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='imoRegNumber'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.imoRegNumber && form.touched.imoRegNumber} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">IMO / Registrasi Number<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='IMO / Registrasi Number' className="form-input" disabled/>
                                                <FormErrorMessage>{form.errors.imoRegNumber}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='vesselName'>
                                {({ field, form }: any) => (
                                    <FormControl isInvalid={form.errors.vesselName && form.touched.vesselName} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Vessel Name' className="form-input" disabled />
                                                <FormErrorMessage>{form.errors.vesselName}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='smc'>
                                {({ form }: any) => (
                                    <FormControl isInvalid={form.errors.smc && form.submitCount} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">SMC</FormLabel>
                                            <div className="form-input-container">
                                                {dataDetail?.smc && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Box
                                                        borderWidth="1px"
                                                        borderRadius="md"
                                                        padding={4}
                                                        backgroundColor="green.50"
                                                        borderColor="green.200"
                                                        width="100%"
                                                        marginBottom="15px"
                                                    >
                                                        <HStack
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={3}
                                                            backgroundColor="white"
                                                            justifyContent="space-between"
                                                            width="100%"
                                                        >
                                                            <HStack>
                                                                <Text>SMC File</Text>
                                                            </HStack>
                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.smc)}>
                                                                <Icon as={FaEye} />
                                                                <Text>Download Document</Text>
                                                            </HStack>
                                                        </HStack>
                                                    </Box>
                                                    {/*(dataDetail?.status !== 2) && <EditIcon
                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                        onClick={() => handleShowEditImage('smc')}
                                                    />*/}
                                                </div>}

                                                {/*(!dataDetail?.smc || editableDocs.includes('smc')) &&
                                                    <div style={{ width: '95%' }}>
                                                        <FileUploader
                                                            onDrop={(e) => props.setFieldValue('smc', e[0])}
                                                            isInvalid={form.errors.smc && form.submitCount}
                                                            type="registration-inspectingcompany"
                                                            isDisabled={props.isSubmitting}
                                                            isPadding={false}
                                                        />
                                                    </div>
                                                */}
                                                <FormErrorMessage>{form.errors.smc}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {role === 'super admin' &&
                                <Field name='status'>
                                    {({ form }: any) => (
                                        <FormControl isInvalid={form.errors.status && form.touched.status} style={{ marginBottom: 15, alignItems: 'center' }}>
                                            <div className="form-field-container">
                                                <FormLabel htmlFor='status-switch' className="form-label">
                                                    Status
                                                </FormLabel>
                                                <div className="form-input-container">
                                                    <SelectApprovalStatus name='status' handleChange={({ value }: any) => props.setFieldValue('status', value)} value={props.values.status} hasError={form.errors.status && form.touched.status} defaultValue={props.values.status} />
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            {props.values.status === "4" &&
                                <Field name='note'>
                                    {({ field, form }: any) => (
                                        <FormControl isInvalid={form.errors.note && form.touched.note} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Note<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Textarea {...field} placeholder='Note' className="form-input" isInvalid={form.errors.note && form.touched.note} />
                                                    <FormErrorMessage>{form.errors.note}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor={'white'}
                                    borderColor={'#D0D5DD'}
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                {role === 'super admin' && <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Update Vessel
                                </Button>}
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default EditVesselShipOwner;
